import styled from 'styled-components'
import { Box, Grid, Flex, Text, Card } from '../../atoms'

export const ChartContainer = styled(Box).attrs({
  as: 'div',
})<{
  $height?: string | number
  $width?: string | number
  $printHeight?: string | number
}>`
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height || '400px'};
  position: relative;

  @media (max-width: 768px) {
    height: ${({ $height }) =>
      typeof $height === 'number' ? `${$height * 0.75}px` : '300px'};
  }

  @media print {
    width: 100% !important;
    height: ${({ $printHeight, $height }) => {
      const h = $printHeight || $height
      return typeof h === 'number' ? `${h}px` : h || '500px'
    }} !important;
    padding: 0 !important;
    margin: 0 !important;
    page-break-inside: avoid;
  }
`

export const ChartSection = styled(Card)`
  height: 100%;
  width: 100%;
  overflow: visible;
  min-width: 0; /* Allow shrinking within grid/flex containers */

  @media print {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    height: auto !important;
    overflow: visible !important;
    border: 1px solid #e2e8f0 !important;
    border-radius: 8px !important;
    box-shadow: none !important;
    padding: 12px !important;
    background: white !important;
  }
`

export const ChartTitle = styled(Text).attrs({
  variant: 'h5',
  weight: 'semibold',
})`
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const ChartGrid = styled(Grid)<{
  columns?: number | string
  gap?: number | string
}>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 350px), 1fr));
  gap: ${({ gap, theme }) =>
    typeof gap === 'number' ? `${gap}px` : gap || theme.spacing.medium};
  margin-bottom: ${({ theme }) => theme.spacing.medium};

  @container (max-width: 700px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 16px !important;
    page-break-inside: avoid;
  }
`

export const LegendContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.large};
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 768px) {
    justify-content: center;
  }
`

export const LegendItem = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const ColorBlock = styled(Box)<{
  $color: string
  $size?: 'small' | 'medium' | 'large'
}>`
  width: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '24px'
      case 'large':
        return '48px'
      default:
        return '40px'
    }
  }};
  height: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '16px'
      case 'large':
        return '24px'
      default:
        return '20px'
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const LegendLabel = styled(Text).attrs({
  variant: 'body2',
})`
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ErrorContainer = styled(Box)<{
  $variant?: 'error' | 'warning' | 'info'
}>`
  padding: ${({ theme }) => theme.spacing.large};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => `${theme.spacing.large} 0`};
  background: ${({ theme: _theme, $variant = 'error' }) => {
    switch ($variant) {
      case 'warning':
        return '#fff3cd'
      case 'info':
        return '#d1ecf1'
      default:
        return '#fee'
    }
  }};
  color: ${({ theme: _theme, $variant = 'error' }) => {
    switch ($variant) {
      case 'warning':
        return '#856404'
      case 'info':
        return '#0c5460'
      default:
        return '#c33'
    }
  }};
`

export const NotAvailableContainer = styled(Box)`
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing.xlarge} ${theme.spacing.large}`};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`

export const NotAvailableTitle = styled(Text).attrs({
  variant: 'h5',
  weight: 'semibold',
})`
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const NotAvailableText = styled(Text).attrs({
  variant: 'body1',
})`
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 500px;
  margin: 0 auto;
`

export const PrintWrapper = styled(Box)`
  @media print {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow: visible !important;

    /* Ensure all child elements respect container bounds */
    & > * {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }

    /* Force canvas elements to respect bounds */
    canvas {
      max-width: 100% !important;
    }

    /* Ensure chart containers are properly sized */
    & > div > div {
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
    }
  }
`

export const ChartHeaderContainer = styled(Flex)`
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  gap: ${({ theme }) => theme.spacing.medium};
`

export const ChartHeaderTitleSection = styled(Box)`
  flex: 1;
  min-width: 0;
`

export const ChartHeaderTitle = styled(Text).attrs({
  variant: 'h5',
  weight: 'semibold',
})`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ChartHeaderSubtitle = styled(Text).attrs({
  variant: 'body2',
})`
  margin-top: ${({ theme }) => theme.spacing.xsmall};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ChartHeaderActions = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex-shrink: 0;

  @media print {
    display: none;
  }
`
